// Keeps the Kula token fresh from the frontend. The browser owns the (httpOnly,
// path-locked) refresh cookie, so only it can mint a new access token. We
// refresh right before sending a request when the token is close to expiry —
// the request then carries a full ~30-min token, so AgentForge (which stores
// and reuses it) can act on the user's behalf for the whole turn without the
// token lapsing mid-workflow. No separate push channel: the fresh token rides
// the request the frontend is already sending.
import { fetchRefreshToken } from '@/lib/auth-interceptor';

const AUTH_KEY = 'apiv1Auth';

// Refresh when the token has this long or less to live (10 min per product
// decision) — comfortably ahead of the 30-min expiry, with slack for clock skew
// and a slow refresh round-trip.
const REFRESH_BUFFER_MS = 10 * 60 * 1000;

// decodeJwtExpMs returns the token's exp in epoch milliseconds, or null for an
// opaque/malformed token (treated as "refresh now").
export const decodeJwtExpMs = (token: string | null): number | null => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

// Coalesce concurrent refreshes (e.g. rapid sends, or a send racing the reactive
// WS event) so /auth/refresh is never called twice at once — Kula rotates the
// refresh token on each call, so a race could invalidate it.
let refreshInFlight: Promise<void> | null = null;

// ensureFreshKulaToken refreshes the Kula access token in place (localStorage)
// when it's within REFRESH_BUFFER_MS of expiry or already gone/unreadable. Call
// it right before sending a request; the request then carries the fresh token.
// Best-effort and coalesced — safe to await from any caller.
export const ensureFreshKulaToken = async (): Promise<void> => {
  const exp = decodeJwtExpMs(localStorage.getItem(AUTH_KEY));
  // Only refresh when we can read the expiry AND it's within the window. An
  // undecodable/absent token (exp === null) is left alone — we don't blindly
  // refresh on every send, and a real Kula access token is always a JWT.
  if (exp === null || exp - Date.now() > REFRESH_BUFFER_MS) {
    return;
  }
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const result = await fetchRefreshToken();
      const fresh = result?.data?.access_token as string | undefined;
      if (fresh) localStorage.setItem(AUTH_KEY, fresh);
    })()
      .catch(() => {
        // fetchRefreshToken is already best-effort (it swallows and may logout).
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  await refreshInFlight;
};

// refreshAndGetKulaToken ensures the token is fresh, then returns the current
// token so the caller can push it to AgentForge over the WS. Returns null if
// there's no token. Used to answer the backend's kula.token_expired ask.
export const refreshAndGetKulaToken = async (): Promise<string | null> => {
  await ensureFreshKulaToken();
  return localStorage.getItem(AUTH_KEY);
};
